<template>
	<!-- Balance and main content -->
	<div class="w-full md:w-3/4 lg:w-4/5 h-full flex flex-col items-start pair-bg overflow-y-auto custom-scrollbar md:rounded-tr-2xl md:rounded-bl-none md:rounded-tl-none rounded-b-2xl z-10">
		<div class="flex items-center justify-between w-full px-4 md:mt-0 mt-6 md:pt-12 md:px-8">
			<div class="flex items-center justify-center ml-4 md:ml-0">
				<img :src="(currentTheme === 'dark' || currentTheme === 'navy') ? darkTransfers : lightTransfers" alt="Wallet Connect Icon" class="w-6 h-6 md:w-8 md:h-8 mr-3"/>
				<h1 class="text-2xl md:text-4xl font-archivomedium text-[var(--text-color)]">
					WalletConnect
				</h1>
				<img :src="selectedChain.icon" alt="Chain Icon" class="hidden md:block w-10 h-10 mb-4 ml-2 mt-4 cursor-pointer md:top-16 md:right-16"/>
				<button @click="$emit('backToOverview')" class="hidden md:block absolute top-8 right-8">
					<img :src="(currentTheme === 'dark' || currentTheme === 'navy') ? darkX : lightX" alt="Close Icon" class="w-3 h-3"/>
				</button>
			</div>
			<!-- back button -->
			<button @click="$emit('backToOverview')" class="block md:hidden ml-auto md:mt-2 mr-4 flex items-center space-x-1 px-3 py-2 text-sm font-archivoregular text-[var(--secondary-text-color)] bg-[var(--accent-color-50)] hover:bg-[var(--accent-color-30)] rounded-full transition-colors">
				<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
				</svg>
				<span class="hover:text-[var(--secondary-text-color)] text-xs">Back</span>
			</button>
		</div>

		<!-- mobile-only chain-pill -->
		<div class="flex justify-start w-full md:hidden mt-4 mb-2 px-8 ">
			<span class="inline-flex items-center space-x-1 bg-[var(--accent-color-10)] text-[var(--accent-color)] px-3 py-2 rounded-full">
				<img :src="selectedChain.icon" alt="Chain Icon" class="w-5 h-5 flex-shrink-0"/>
				<span class="text-sm font-archivoregular">
					{{ selectedChain.name }}
				</span>
			</span>
		</div>

		<div class="mb-6 mx-auto w-full md:w-[80%] lg:w-[65%] xl:w-[50%] p-4">
			<div class="hidden md:flex md:flex-row md:items-center md:justify-center md:space-x-2 mb-6 mt-2">
				<h1 class="text-lg md:text-xl font-archivomedium text-[var(--text-color)]">
					Connect to dApps via WalletConnect
				</h1>
				<img :src="walletConnectLogo1" alt="WalletConnect Logo" class="w-8 h-8 rounded-full border border-blue-300"/>
			</div>

			<div class="relative">
				<input :value="walletConnectUri" @input="$emit('update:walletConnectUri', $event.target.value)" type="text" placeholder="Enter Wallet Connect URI" class="w-5/6 border rounded-l hover:opacity-80 p-2 focus:outline-none focus:ring"/>
				<button @click="$emit('startScanWC')" class="w-1/6 p-2 absolute top-0 right-0 h-full text-sm font-archivoregular bg-[var(--send-max-color)] border-[var(--send-max-color)] text-[var(--hover-text-color)] rounded-r hover:opacity-80">
					<img :src="(currentTheme === 'dark' || currentTheme === 'navy') ? darkScan : lightScan" alt="Scan Icon" class="w-6 h-6 mx-auto"/>
				</button>
			</div>

			<button @click="prepareConnect" class="mt-6 font-archivoregular w-full bg-transparent text-[var(--accent-color)] border border-[var(--accent-color)] py-2 rounded-full hover:bg-[var(--accent-color)] hover:text-[var(--hover-text-color)] transition duration-200 shadow-inner">
				Connect Cardware Device
			</button>

			<!--  Connected Sessions Panel  -->
			<div class="mb-6 mt-2 mx-auto">
				<div class="bg-transparent rounded-lg p-4">
					<h2 class="text-lg font-archivomedium text-left text-[var(--text-color)] my-4">
						Connected Sessions
					</h2>
					<!-- Tabs -->
					<div class="flex justify-start space-x-8 border-b border-[var(--border-color)] mb-6">
						<button @click="activeTab = 'connected'" class="pb-2 font-archivoregular transition-colors" :class="activeTab === 'connected' ? 'text-[var(--accent-color)] border-b-2 border-[var(--accent-color)]' : 'text-[var(--secondary-text-color)]'">
							Connected
						</button>
						<button @click="activeTab = 'past'" class="pb-2 font-archivoregular transition-colors" :class="activeTab === 'past' ? 'text-[var(--accent-color)] border-b-2 border-[var(--accent-color)]' : 'text-[var(--secondary-text-color)]'">
							Past
						</button>
					</div>


					<!-- Connected Tab -->
					<div v-if="activeTab === 'connected'">
						<div v-if="!Object.keys(wcSessions).length" class="text-sm text-[var(--secondary-text-color)]">
							No active sessions
						</div>
						<div v-for="(session, topic) in wcSessions" :key="topic" class="flex items-center justify-between mb-3 p-3 bg-white bg-opacity-10 rounded-lg hover:bg-opacity-20 transition">
							<div class="flex items-center space-x-2">
								<span class="pulse-dot"></span>
								<span class="text-sm text-[var(--accent-color)]">
									Connected to: {{ session.name }}
								</span>
								<img v-if="session.icon" :src="session.icon" alt="Current Session Icons" class="w-6 h-6 rounded"/>
							</div>
							<div class="relative">
								<button @click="openMenu(topic)" class="p-1 hover:bg-[var(--dropdown-hover-color)] rounded-full">
									<!-- vertical three-dots icon -->
									<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[var(--secondary-text-color)]" fill="currentColor" viewBox="0 0 20 20">
										<path d="M6 10a2 2 0 114 0 2 2 0 01-4 0zm4 0a2 2 0 114 0 2 2 0 01-4 0z"/>
									</svg>
								</button>
								<transition name="fade">
									<div v-if="menuOpen === topic" class="absolute right-0 mt-2 w-48 bg-[var(--dropdown-bg-color)] rounded-lg shadow-lg z-20">
										<button @click="disconnect(topic)" class="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-100 rounded-lg">
											Disconnect from {{ session.name }}
										</button>
									</div>
								</transition>
							</div>
						</div>

						<!-- Disconnect All -->
						<div v-if="Object.keys(wcSessions).length" class="mt-4 text-right">
							<button @click="disconnectAll" :disabled="isDisconnectingAll" class="inline-flex items-center space-x-1 text-sm text-[var(--accent-color)] hover:underline">
								<span>
									{{ isDisconnectingAll ? 'Disconnecting...' : 'Disconnect All Sessions' }}
								</span>
							</button>
							<!--<button @click="disconnectAll" class="inline-flex items-center space-x-1 text-sm text-[var(--accent-color)] hover:underline">
								<span>
									Disconnect All Sessions
								</span>
							</button>-->
						</div>
					</div>
					<!-- Past Tab -->
					<div v-if="activeTab === 'past'">
						<div v-if="!pastInlineSessions.length" class="text-sm text-[var(--secondary-text-color)]">
							No past sessions
						</div>
						<div v-for="(session, idx) in pastInlineSessions" :key="session.topic || idx" class="flex items-center justify-between mb-3 p-3 bg-white bg-opacity-5 rounded-lg transition">
							<div class="flex items-center space-x-2">
								<span class="inline-block w-3 h-3 bg-[#E38B8B] rounded-full"></span>
								<span class="text-sm text-[var(--text-color)] font-archivoregular">
									Disconnected from <span class="text-[#E38B8B]">{{ session.name }}</span>
								</span>
								<img v-if="session.icon" :src="session.icon" alt="Past Session Icons" class="w-6 h-6 rounded"/>
							</div>
						</div>

					<!-- See More button -->
					<div v-if="activeTab === 'past' && pastSessions.length > 3" class="text-center mt-2">
						<button @click="showPastSessionsPopup = true"
						class="px-4 py-2 text-sm font-archivoregular text-[var(--accent-color)] hover:underline">
							See More
						</button>
						</div>
					</div>
				</div>
			</div>


			<!-- Info Block: How To Guides -->
			<div ref="howToRef" class="relative mt-6">
				<div @click.stop="showHowTo = !showHowTo" class="flex items-center justify-between p-4 bg-[var(--dApp-bg-color)] rounded-lg cursor-pointer">
					<div class="flex items-center space-x-2">
						<!-- Info Icon -->
						<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-[var(--accent-color)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 1010 10A10 10 0 0012 2z" />
						</svg>
						<span class="font-archivomedium text-[var(--text-color)]">How To Guides</span>
					</div>
					<svg xmlns="http://www.w3.org/2000/svg" :class="{ 'transform rotate-180': showHowTo }" class="w-5 h-5 text-[var(--text-color)] transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
					</svg>
				</div>
				<transition name="fade">
					<div v-if="showHowTo" class="p-4 mt-2 text-sm text-[var(--secondary-text-color)] bg-[var(--dApp-bg-color)] rounded-lg flex flex-col">
						<p class="my-4 text-sm md:text-md text-left font-archivolight text-[var(--secondary-text-color)]">
							<strong class="font-archivomedium">Trade on your preferred dApp, and Sign with Cardware.</strong>
						</p>

						<ul class="list-disc pl-5 my-4 text-sm md:text-md font-archivolight text-[var(--secondary-text-color)] space-y-1">
							<li><strong class="font-archivomedium">Step 1)</strong> Visit dApp and click on Connect Wallet</li>
							<li><strong class="font-archivomedium">Step 2)</strong> Click on the WalletConnect option</li>
							<li><strong class="font-archivomedium">Step 3)</strong> Using the Cardware Web App, either enter the WalletConnect URI, or scan the WalletConnect QR code</li>
						</ul>

						<a href="https://www.youtube.com/watch?v=IGijgGBodBI" target="_blank" class="my-2 flex items-center justify-center mx-auto font-archivoregular w-1/2 bg-transparent text-[var(--accent-color)] border border-[var(--accent-color)] py-2 rounded-full hover:bg-[var(--accent-color)] hover:text-[var(--hover-text-color)] transition duration-200 shadow-inner">Video Guides</a>
					</div>
				</transition>
			</div>

			<!-- dApps -->
			<div ref="supportedRef" class="relative mt-4">
				<div @click.stop="showSupported = !showSupported" class="flex items-center justify-between p-4 bg-[var(--dApp-bg-color)] rounded-lg cursor-pointer">
					<div class="flex items-center space-x-2">
						<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-[var(--accent-color)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 1010 10A10 10 0 0012 2z" />
						</svg>
						<span class="font-archivomedium text-[var(--text-color)]">
							Connect To dApps
						</span>
					</div>
					<svg xmlns="http://www.w3.org/2000/svg" :class="{ 'transform rotate-180': showSupported }" class="w-5 h-5 text-[var(--text-color)] transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
					</svg>
				</div>
				<transition name="fade">
					<div v-if="showSupported" class="mt-2">
						<!-- <h3 class="text-md md:text-lg text-center font-archivoregular text-[var(--text-color)] my-4">
							Connect To dApps:
						</h3> -->
						<div class="relative">
							<div class="max-h-64 overflow-y-auto rounded-lg border border-[var(--new-border-color)] bg-[var(--dApp-bg-color)] shadow-lg">
								<div class="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 mb-8">
									<template v-for="(dapp, i) in supportedDApps" :key="i + '-' + dapp.url">
										<a :href="dapp.url" target="_blank" rel="noopener noreferrer" class="flex flex-col items-center justify-center p-3 bg-[var(--trade-popup-bg-color)] rounded-lg shadow hover:shadow-md hover:bg-[var(--new-dropdown-bg-color)] transition">
											<img :src="dapp.src" alt="dApp Logo" class="h-12 w-12 object-contain mb-2" />
											<span class="text-sm font-archivoregular text-[var(--text-color)]">
												{{ dapp.name }}
											</span>
										</a>
									</template>
								</div>
							</div>
							<!-- gradient -->
							<div class="pointer-events-none absolute bottom-0 left-0 w-full h-20 rounded-lg" style="background: linear-gradient(to top, rgba(128,128,128,0.2), rgba(128,128,128,0.1), transparent);">
							</div>
						</div>
					</div>
				</transition>
			</div>
		</div>
	</div>

	<!-- Past Sessions Popup -->
	<div v-if="showPastSessionsPopup" class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50">
		<div class="popup-bg text-[var(--popup-text-color)] rounded-lg p-8 w-10/12 md:w-3/6 lg:w-1/3 xl:w-1/4 relative">
			<button @click="showPastSessionsPopup = false" class="absolute top-4 right-4">
				<img :src="(currentTheme === 'dark' || currentTheme === 'navy') ? darkX : lightX" alt="Close" class="w-3 h-3"/>
			</button>
			<h2 class="text-2xl font-archivomedium mb-4 text-center">
				Past Sessions
			</h2>
			<div class="flex flex-col space-y-3 max-h-64 overflow-y-auto custom-scrollbar">
				<div v-for="(session, idx) in pastSessions" :key="session.topic || idx"
				class="flex items-center justify-between p-3 bg-[var(--dApp-bg-color)] rounded-lg">
					<div class="flex items-center space-x-2">
						<span class="inline-block w-3 h-3 bg-[#E38B8B] rounded-full"></span>
						<span class="text-sm text-[var(--text-color)] font-archivoregular">
							Disconnected from <span class="text-[#E38B8B]">{{ session.name }}</span>
						</span>
						<img v-if="session.icon" :src="session.icon" alt="Session Icon" class="w-6 h-6 rounded"/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
	import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
	import lightTransfers from '../assets/wcLogoLightTheme.png';
	import darkTransfers from '../assets/wcLogoDarkTheme.png';
	import lightX from '../assets/X.png';
	import darkX from '../assets/XWhite.png';
	import lightScan from '../assets/QR_Light_Theme.png';
	import darkScan from '../assets/QR_Dark_Theme.png';
	import walletConnectLogo1 from '../assets/WalletConnectLogo1.png';

	const pastSessions = ref([]);
	const showPastSessionsPopup = ref(false);
	const pastInlineSessions = computed(() => pastSessions.value.slice(0, 3));

	const isDisconnectingAll = ref(false);

	const showHowTo = ref(false);
	const showSupported = ref(false);
	const activeTab = ref('connected');
	const menuOpen = ref(null);

	const howToRef = ref(null);
	const supportedRef = ref(null);

	const pastDisplayCount = ref(3);

	const displayedPastSessions = computed(() =>
		pastSessions.value.slice(0, pastDisplayCount.value)
	);

	onMounted(() => {
		document.addEventListener('click', onClickOutsideTrade);
		const saved = JSON.parse(localStorage.getItem('cardware_wc_past_sessions') || '[]');
		pastSessions.value = Array.isArray(saved) ? saved : [];
	});

	onBeforeUnmount(() => {
		document.removeEventListener('click', onClickOutsideTrade);
	});

	const props = defineProps({
		currentTheme: { type: String, required: true },
		supportedDApps: { type: Array, required: true },
		walletConnectUri: { type: String, required: true },
		btcOnly: { type: Boolean, required: true },
		isMobileMenuOpen: { type: Boolean, required: true },
		chainDropdownOpen: { type: Boolean, required: true },
		displayedChains: { type: Array, required: true },
		selectedChain: { type: Object, required: true },
		wcSessions: { type: Object, required: true },
	});

	const emit = defineEmits([
		'update:walletConnectUri',
		'startScanWC',
		'connectWC',
		'disconnectWC',
		'disconnectAllWC',
		'backToOverview',
		'toggleMobileMenu',
		'closeMobileMenu',
		'toggleChainDropdown',
		'selectChain',
		'openTradeView',
		'showThemePopup',
		'showResourcesPopup',
		'showComingSoonMessage',
		'showExportZpubView',
		'unpairWallet'
	]);

	const prepareConnect = () => {
		emit('connectWC');
	}

	const openMenu = (topic) => {
		menuOpen.value = menuOpen.value === topic ? null : topic;
	}

	const disconnect = (topic) => {
		const session = props.wcSessions[topic];
		if (session) {
			pastSessions.value.unshift({ topic, ...session });
			localStorage.setItem('cardware_wc_past_sessions', JSON.stringify(pastSessions.value));
		}
		emit('disconnectWC', topic);
		menuOpen.value = null;
	}

	/*const disconnectAll = () => {
		Object.entries(props.wcSessions).forEach(([topic, session]) => {
			pastSessions.value.unshift({ topic, ...session });
		});
		localStorage.setItem('cardware_wc_past_sessions', JSON.stringify(pastSessions.value));
		emit('disconnectAllWC');
		menuOpen.value = null;
	}*/

	const disconnectAll = () => {
		// flip “loading” on
		isDisconnectingAll.value = true;

		// stash past sessions locally
		Object.entries(props.wcSessions).forEach(([topic, session]) => {
			pastSessions.value.unshift({ topic, ...session });
		});
		localStorage.setItem('cardware_wc_past_sessions', JSON.stringify(pastSessions.value));

		// ask parent to tear them all down
		emit('disconnectAllWC');
			menuOpen.value = null;
		}

		// once the parent empties out wcSessions, clear our “loading” flag:
		watch(() => Object.keys(props.wcSessions).length,
			(len) => {
				if (isDisconnectingAll.value && len === 0) {
				isDisconnectingAll.value = false;
			}
		}
	);

	const loadMorePast = () => {
		pastDisplayCount.value = Math.min(
			pastDisplayCount.value + 3,
			pastSessions.value.length
		);
	}

	function onClickOutsideTrade(e) {
		if (showHowTo.value && howToRef.value && !howToRef.value.contains(e.target)) {
			showHowTo.value = false;
		}
		if (showSupported.value && supportedRef.value && !supportedRef.value.contains(e.target)) {
			showSupported.value = false;
		}
	}


</script>

<style scoped>
	.carousel-track {
		animation: scroll-left 12s linear infinite;
	}
	@keyframes scroll-left {
		0%   { transform: translateX(0); }
		100% { transform: translateX(-50%); }
	}

	.pair-bg {
		background: var(--main-bg-color);
	}

	/* popup background */
	.popup-bg {
		background: var(--popup-bg-color);
	}

	.fade-enter-active,
	.fade-leave-active {
		transition: all 0.2s ease;
	}
	.fade-enter-from,
	.fade-leave-to {
		opacity: 0;
		transform: scaleY(0.9);
	}
	.fade-enter-to,
	.fade-leave-from {
		opacity: 1;
		transform: scaleY(1);
	}
	.pulse-dot {
		display: inline-block;
		width: 12px; height: 12px;
		background-color: #38a169;
		border-radius: 50%;
		animation: pulse 1.5s ease-out infinite;
	}
	@keyframes pulse {
		0% { transform: scale(0.8); opacity: 0.6; }
		50% { transform: scale(1.2); opacity: 1;   }
		100% { transform: scale(0.8); opacity: 0.6; }
	}

	.custom-scrollbar {
		/* firefox */
		overflow-y: auto;
		scrollbar-width: thin;
		scrollbar-color: #888 #f1f1f1;
	}

	/* Chrome, Edge, and Safari */
	.custom-scrollbar::-webkit-scrollbar {
		width: 8px;                        
	}

	.custom-scrollbar::-webkit-scrollbar-track {
		background: #f1f1f1;
		border-radius: 16px;
		margin-top: 5%;
		margin-bottom: 5%;
	}

	.custom-scrollbar::-webkit-scrollbar-thumb {
		background-color: #888;            
		border-radius: 8px;
	}

	.custom-scrollbar::-webkit-scrollbar-thumb:hover {
		background-color: #555;           
	}
</style>
